import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type ListContent, type ListItemContent, type ListKind } from "@vmeste/shared";
import { useSession } from "../app/session";
import { useLists, useMembers } from "../app/lists";
import { useTags } from "../app/tags";
import { useChat } from "../app/chat";
import { type ListItemView, type ListView, type MemberView } from "../app/views";
import { t, useDateFormats, useT, type MessageKey } from "../i18n/index.js";
import { AddButton, Button, Card } from "../components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useScreenAwake } from "../app/wakelock";
import { Sheet } from "../components/Sheet";
import { ScreenHeader } from "../components/ScreenHeader";
import {
  closeDraft,
  minimiseDraft,
  openDraft,
  patchDraft,
  useDraftValues,
} from "../app/draft";
import { TagPicker } from "../components/TagPicker";
import { AttachmentStrip } from "../components/AttachmentStrip";
import {
  ChatIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  EditIcon,
  GripIcon,
  PlusIcon,
  ArchiveIcon,
  TrashIcon,
  UndoIcon,
} from "../components/icons";
import { operations } from "../lib/outbox";
import { syncEngine } from "../lib/sync";
import { cn } from "../lib/cn";

// Wishes are their own section, not a third preset — see SPEC §4.2.
/** How long the second press stays armed. Long enough to mean it, short
    enough that a card left alone is not still dangerous. */
const CONFIRM_TIMEOUT_MS = 4_000;

const KINDS: ListKind[] = ["todo", "shopping"];

// The per-kind wording is a message key rather than a string: the words are
// looked up when the row renders, so a language change reaches them.
const KIND_LABEL: Record<ListKind, MessageKey> = {
  todo: "lists.kindTodo",
  shopping: "lists.kindShopping",
};

const PLACEHOLDER: Record<ListKind, MessageKey> = {
  todo: "lists.placeholderTodo",
  shopping: "lists.placeholderShopping",
};

/** What the first list of each kind is called if the person just hits enter. */
/** A list being named. The name may stay empty — see `startList`. */
interface ListDraft {
  title: string;
}

const DEFAULT_TITLE: Record<ListKind, MessageKey> = {
  todo: "lists.defaultTitleTodo",
  shopping: "lists.defaultTitleShopping",
};

const NEW_LIST_LABEL: Record<ListKind, MessageKey> = {
  todo: "lists.newListLabelTodo",
  shopping: "lists.newListLabelShopping",
};

/** What a folded list says instead of its rows. */
/** «Дело» or «Покупка» — the sheet is one component and the word is not. */
const ITEM_LABEL: Record<ListKind, MessageKey> = {
  todo: "lists.fieldText",
  shopping: "lists.fieldItem",
};

const REMAINING: Record<ListKind, MessageKey> = {
  todo: "lists.remainingTodo",
  shopping: "lists.remainingShopping",
};

/**
 * Which lists this device has folded shut.
 *
 * `localStorage` and deliberately not an operation. Folding is how one phone is
 * looking at the space this minute, not something true about the list — pushed
 * through the outbox it would reach across and close a list under the other
 * person's thumb.
 */
const COLLAPSED_KEY = "vmeste:lists:collapsed";

/**
 * Which of the two kinds this device was last looking at.
 *
 * The same argument the folded lists make one line above, and the same one the
 * calendar's zoom makes: a choice about *where you are* has to survive going
 * to another screen and coming back. Whoever lives in «Покупки» was paying a
 * tap for it on every single visit.
 *
 * Not the wishes' filters, which deliberately reset — a filter is a look at
 * something for a minute, and coming back to half a list hidden by a choice
 * made last week is worse than re-applying it. A tab is a place, not a look.
 */
const KIND_KEY = "vmeste:lists:kind";

function readKind(): ListKind {
  try {
    const stored = localStorage.getItem(KIND_KEY);
    return stored === "shopping" || stored === "todo" ? stored : "todo";
  } catch {
    return "todo";
  }
}

function useListKind(): [ListKind, (kind: ListKind) => void] {
  const [kind, setKind] = useState(readKind);

  useEffect(() => {
    try {
      localStorage.setItem(KIND_KEY, kind);
    } catch {
      /* private mode, storage off — the tab simply stops being remembered */
    }
  }, [kind]);

  return [kind, setKind];
}

function readCollapsed(): ReadonlySet<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY);
    const stored: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(stored)) return new Set();

    return new Set(stored.filter((id): id is string => typeof id === "string"));
  } catch {
    // Private mode, storage turned off, or something unreadable left at our
    // key. An unfolded screen is the right thing to fall back to, not a crash.
    return new Set();
  }
}

function useCollapsedLists(): {
  collapsed: ReadonlySet<string>;
  toggle: (id: string) => void;
} {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  // Written from an effect rather than from the updater: React runs an updater
  // twice under StrictMode, and a write is not the kind of thing to do twice.
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]));
    } catch {
      // Nothing to do about it, and nothing worth losing a render over.
    }
  }, [collapsed]);

  const toggle = useCallback((id: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  return { collapsed, toggle };
}

/**
 * A sideways swipe that never takes the page's scroll with it.
 *
 * Three things had to be true before a gesture was worth shipping here at all.
 *
 *   - **It must not eat a scroll.** Nothing below calls `preventDefault` and
 *     nothing sets `touch-action`, so the browser stays free to scroll the page
 *     at any moment. When it decides to, it sends `pointercancel` and the
 *     gesture is dropped — the scroll wins by default rather than by our leave.
 *   - **A diagonal is not a swipe.** The axis is decided once, at ten pixels of
 *     travel, and horizontal has to win by half again to be believed. Anything
 *     less is read as the beginning of a scroll and never looked at again.
 *   - **It must not touch dragging.** This lives on the card's heading row;
 *     dnd-kit lives on the item rows, inside a `DndContext` that is a different
 *     subtree entirely. The two never see the same pointer.
 *
 * The tap that follows a swipe is swallowed, or a reveal would also fold the
 * card shut on the way past.
 */
const SWIPE_DECIDE_PX = 10;
const SWIPE_TRIGGER_PX = 44;
const SWIPE_CLICK_MS = 500;

interface SwipeHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onClickCapture: (event: ReactMouseEvent) => void;
}

function useSwipe(onSwipe: (direction: 1 | -1) => void): SwipeHandlers {
  const gesture = useRef<{ x: number; y: number; axis: "" | "x" | "y" } | null>(null);
  const swipedAt = useRef(0);

  const onPointerDown = useCallback((event: ReactPointerEvent) => {
    gesture.current = { x: event.clientX, y: event.clientY, axis: "" };
    swipedAt.current = 0;
  }, []);

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      const from = gesture.current;
      if (!from) return;

      const dx = event.clientX - from.x;
      const dy = event.clientY - from.y;

      if (!from.axis) {
        if (Math.abs(dx) < SWIPE_DECIDE_PX && Math.abs(dy) < SWIPE_DECIDE_PX) return;
        from.axis = Math.abs(dx) > Math.abs(dy) * 1.5 ? "x" : "y";
      }

      if (from.axis !== "x" || Math.abs(dx) < SWIPE_TRIGGER_PX) return;

      // Fired once per gesture: the finger carrying on to the edge of the
      // screen means the same thing as stopping at fifty pixels.
      gesture.current = null;
      swipedAt.current = event.timeStamp;
      onSwipe(dx < 0 ? -1 : 1);
    },
    [onSwipe],
  );

  const onPointerEnd = useCallback(() => {
    gesture.current = null;
  }, []);

  const onClickCapture = useCallback((event: ReactMouseEvent) => {
    // A window rather than a flag. The click a swipe leaves behind lands within
    // a frame or two of it; a flag left standing would instead swallow whatever
    // was pressed next — an Enter on the keyboard, minutes later, on a phone
    // that had been swiped once.
    if (event.timeStamp - swipedAt.current > SWIPE_CLICK_MS) return;

    swipedAt.current = 0;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: onPointerEnd,
    onPointerCancel: onPointerEnd,
    onClickCapture,
  };
}

export function ListsPage() {
  const t = useT();
  const { session } = useSession();
  const { listsFor, createList } = useLists();
  const [kind, setKind] = useListKind();
  const { collapsed, toggle } = useCollapsedLists();

  /*
   * The form's state is not this screen's — see `app/draft`. Keyed by kind, so
   * a half-named shopping list and a half-named to-do list are two different
   * things and the second cannot quietly replace the first.
   */
  const draftKey = `list.new.${kind}`;
  const adding = useDraftValues<ListDraft>(draftKey);

  const startList = () =>
    openDraft<ListDraft>({
      key: draftKey,
      path: "/lists",
      title: t("lists.newList"),
      values: { title: "" },
      // A list with no name is allowed: it gets the default one, and that is
      // the fastest way to start a shopping list from a supermarket door.
      isReady: () => true,
      submit: (input) =>
        createList(spaceId, kind, input.title.trim() || t(DEFAULT_TITLE[kind])),
    });

  const spaceId = session?.spaceId ?? "";
  const lists = listsFor(spaceId);

  /*
   * Arriving from a search hit or the activity feed.
   *
   * Both used to hand out `/lists` and nothing else: found «кофеварку» among
   * forty rows, tapped it, and landed at the top of the screen to look for it
   * again by eye. The chat has had a receiver for this since the beginning
   * (`?topic=`), which is what every «Обсудить» button uses; the other screens
   * simply never grew one.
   *
   * Read once and then dropped from the address, because it is an arrival and
   * not a place: a reload, a back button or a shared link should not keep
   * re-opening the same row.
   */
  const [params, setParams] = useSearchParams();
  const wantedList = params.get("list");
  const wantedItem = params.get("item");
  const [arrival, setArrival] = useState<{ list: string; item: string | null } | null>(null);

  useEffect(() => {
    if (!wantedList) return;

    setArrival({ list: wantedList, item: wantedItem });
    setParams(new URLSearchParams(), { replace: true });
  }, [wantedList, wantedItem, setParams]);

  // The tab has to be the one the row lives on, or it is not on screen at all.
  useEffect(() => {
    if (!arrival) return;
    const target = lists.find((list) => list.id === arrival.list);
    if (target) setKind(target.kind);
  }, [arrival, lists, setKind]);

  const ofKind = useMemo(() => lists.filter((l) => l.kind === kind), [lists, kind]);
  const visible = useMemo(() => ofKind.filter((l) => !l.archived), [ofKind]);
  const archived = useMemo(() => ofKind.filter((l) => l.archived), [ofKind]);

  /* Folded by default, because an archive that is open is not an archive. */
  const [showArchive, setShowArchive] = useState(false);

  /*
   * A shopping list open in a shop is the one screen here that is held and not
   * touched — a phone in one hand, a basket in the other. Only for the
   * shopping preset: a list of chores is read at a desk, where the screen going
   * dark costs nothing.
   */
  useScreenAwake(kind === "shopping" && visible.some((list) => !collapsed.has(list.id)));

  return (
    <div className="flex flex-col gap-5 px-4 pt-4">
      <ScreenHeader
        title={t("lists.title")}
        subtitle={t("lists.subtitle")}
        /* Not while the screen is empty: the empty state offers the same door
           with the words on it, and two buttons called «Новый список» a
           thumb's width apart is one too many. */
        /* Absent only while the screen is empty — the empty state offers the
           same door with words on it, and two buttons called «Новый список» a
           thumb's width apart is one too many. While the form is open it stays
           and dims: see `AddButton`. */
        action={
          visible.length === 0 ? null : (
            <AddButton
              dimmed={adding !== null}
              label={t("lists.newList")}
              onClick={startList}
            />
          )
        }
      />

      {/* One mechanism, three presets (SPEC §4.2) — so this is a filter over the
          same list type, not three different screens.

          A pill holding pills, and no line anywhere: the track is one tonal step
          off the ground, and the chosen segment is the only amber on the screen
          until something else asks to be acted on. */}
      <div
        role="tablist"
        aria-label={t("lists.kindLabel")}
        className="flex gap-1 rounded-pill bg-surface p-1.5"
      >
        {KINDS.map((k) => (
          <button
            key={k}
            role="tab"
            aria-selected={k === kind}
            onClick={() => setKind(k)}
            className={cn(
              "min-h-[var(--touch)] flex-1 rounded-pill px-4 text-[0.9375rem] transition-colors",
              k === kind
                ? "bg-accent-soft font-semibold text-accent-ink"
                : "font-medium text-muted hover:text-ink-soft",
            )}
          >
            {t(KIND_LABEL[k])}
          </button>
        ))}
      </div>

      {visible.map((list) => (
        <ListCard
          key={list.id}
          spaceId={spaceId}
          list={list}
          open={!collapsed.has(list.id) || arrival?.list === list.id}
          onToggle={() => toggle(list.id)}
          arrivedAt={arrival?.list === list.id ? (arrival.item ?? true) : null}
          onArrived={() => setArrival(null)}
        />
      ))}

      {archived.length > 0 ? (
        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setShowArchive((was) => !was)}
            aria-expanded={showArchive}
            className="inline-flex min-h-[var(--touch)] w-fit items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] font-medium text-muted transition-colors hover:text-ink-soft"
          >
            <ChevronDownIcon
              aria-hidden="true"
              strokeWidth={1.9}
              className={cn("size-[18px] transition-transform", showArchive && "rotate-180")}
            />
            {t("lists.archiveHeading", { count: archived.length })}
          </button>

          {showArchive
            ? archived.map((list) => (
                <ListCard
                  key={list.id}
                  spaceId={spaceId}
                  list={list}
                  open={false}
                  onToggle={() => toggle(list.id)}
                  arrivedAt={null}
                  onArrived={() => setArrival(null)}
                />
              ))
            : null}
        </div>
      ) : null}

      <NewList
        kind={kind}
        values={adding}
        onChange={patchDraft}
        first={visible.length === 0}
        onOpen={startList}
        onClose={closeDraft}
        onMinimise={minimiseDraft}
        onCreate={(title) => {
          closeDraft();
          createList(spaceId, kind, title);
        }}
      />
    </div>
  );
}

/**
 * Creating a list.
 *
 * This screen previously had no way to make one, so a fresh space was a dead
 * end: an empty state with nothing to press. An invited person landing there
 * would have had no path forward at all.
 */
function NewList({
  kind,
  values,
  onChange,
  first,
  onOpen,
  onClose,
  onMinimise,
  onCreate,
}: {
  kind: ListKind;
  /** Null while nothing is being written — see `app/draft`. */
  values: ListDraft | null;
  onChange: (next: ListDraft) => void;
  first: boolean;
  onOpen: () => void;
  onClose: () => void;
  onMinimise: () => void;
  onCreate: (title: string) => void;
}) {
  const t = useT();
  const title = values?.title ?? "";

  function submit(event: FormEvent) {
    event.preventDefault();
    onCreate(title.trim() || t(DEFAULT_TITLE[kind]));
  }

  if (!values) {
    /*
     * Nothing at all once there is something on the screen — the «+» in the
     * heading is the way in now. The empty screen keeps a button, and keeps it
     * spelled out: an icon alone on an otherwise blank screen says where to
     * press but not what happens.
     */
    if (!first) return null;

    return (
      <div className="flex flex-col items-center gap-4 px-1 py-12 text-center">
        <p className="max-w-xs text-balance text-ink-soft">
          {t("lists.empty", { title: t(DEFAULT_TITLE[kind]) })}
        </p>
        <Button variant="secondary" onClick={onOpen}>
          <PlusIcon className="size-[20px]" strokeWidth={1.9} />
          {t("lists.newList")}
        </Button>
      </div>
    );
  }

  return (
    <Sheet title={t("lists.newList")} onClose={onClose} onMinimise={onMinimise}>
      {/* The field is a step lighter than the sheet that holds it — the same
          tonal nesting TextField uses, so a real form field reads as one
          without a box drawn round it. */}
      <form onSubmit={submit} className="flex flex-col gap-3 p-4">
        <div className="flex min-h-[var(--touch)] items-center gap-2 rounded-row bg-surface-alt px-3">
          <PlusIcon
            aria-hidden="true"
            className="size-[20px] shrink-0 text-muted"
            strokeWidth={1.9}
          />
          <input
            autoFocus
            value={title}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder={t(DEFAULT_TITLE[kind])}
            aria-label={t(NEW_LIST_LABEL[kind])}
            className="min-h-[var(--touch)] min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          {/* An explicit way out rather than closing on blur. Blur fires before
              the submit click lands, which made creating a list with the default
              name impossible — and any fix by timing is a race waiting to come
              back. */}
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit">{t("lists.create")}</Button>
        </div>
      </form>
    </Sheet>
  );
}

function ListCard({
  spaceId,
  list,
  open,
  onToggle,
  arrivedAt,
  onArrived,
}: {
  spaceId: string;
  list: ListView;
  open: boolean;
  onToggle: () => void;
  /**
   * Somebody was sent here. `true` for the list itself, an item id for a row
   * inside it, `null` for the ordinary case — which is every other card.
   */
  arrivedAt: string | true | null;
  onArrived: () => void;
}) {
  const t = useT();
  const cardRef = useRef<HTMLElement>(null);

  /*
   * Bring the card into view once, and let go of the arrival.
   *
   * `onArrived` clears it in the screen above, so nothing here has to remember
   * whether it has already scrolled — and a card the person then scrolls away
   * from is not dragged back.
   */
  useEffect(() => {
    if (!arrivedAt) return;
    cardRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
    // After the scroll is started, not before: the row inside needs the card
    // open, and the card opens in the same render.
    const timer = setTimeout(onArrived, 600);
    return () => clearTimeout(timer);
  }, [arrivedAt, onArrived]);
  const { session } = useSession();
  const { addItem, toggleItem, removeItem, moveItem, removeList, archiveList } =
    useLists();
  const members = useMembers(spaceId);

  /**
   * The draft lives in the DOM, not in React state.
   *
   * As controlled state, clearing it after a submit triggered a re-render that
   * overwrote whatever had been typed since — so entering items quickly lost
   * every second one. The value here only matters at submit, which is exactly
   * what an uncontrolled input is for; `hasText` exists purely to show the
   * button, and re-rendering on it can no longer disturb the field.
   */
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasText, setHasText] = useState(false);

  // Folding takes the add row off the screen with everything else, and an
  // uncontrolled field comes back empty — so the «Добавить» button must not
  // still be sitting there from before, promising to add nothing.
  useEffect(() => {
    if (!open) setHasText(false);
  }, [open]);

  const bodyId = useId();
  const actionsId = useId();
  const userId = session?.user.id ?? "";
  const total = list.items.length;
  const done = list.items.filter((i) => i.done).length;
  const left = total - done;
  const complete = total > 0 && left === 0;

  /**
   * Renaming and deleting, revealed by moving the row aside.
   *
   * They used to drop into a drawer *below* the heading, and the gesture and
   * the result did not match: you pushed the row sideways and something opened
   * underneath, which reads as «редактирование началось» rather than «вот две
   * кнопки». Now the row actually moves and the two buttons are behind it,
   * where the thumb was already going.
   *
   * Two ways in, and they open the same thing. The button in the heading row is
   * the one that can be found — by a thumb, by a mouse, by a tab key; the swipe
   * is the shortcut for whoever already knows it is there. A gesture cannot be
   * the only door, because a gesture nobody has been told about is not a door.
   *
   * And one obvious way out: while the row is aside, a tap anywhere on it puts
   * it back instead of folding the list. Closing was the half nobody could
   * find — the gesture that opens has no visible opposite.
   */
  const [actions, setActions] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const openActions = useCallback((direction: 1 | -1) => {
    setActions(direction === -1);
    if (direction === 1) setRenaming(false);
  }, []);

  const swipe = useSwipe(openActions);

  // Completed items sink to the bottom; the open ones are the reason you opened
  // the list. Ordering inside each group stays the stored `position` order.
  const ordered = useMemo(
    () => [...list.items].sort((a, b) => Number(a.done) - Number(b.done)),
    [list.items],
  );

  /**
   * Only the open items are draggable.
   *
   * Done ones sink to their own group, so dropping "between" two of them would
   * mean a position derived from neighbours the person is not looking at.
   */
  const draggable = useMemo(() => ordered.filter((item) => !item.done), [ordered]);

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, or tapping a checkbox on a
    // phone would be read as the beginning of one.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const to = draggable.findIndex((item) => item.id === over.id);
    if (to === -1) return;

    moveItem(spaceId, list.id, String(active.id), to);
  }

  function onAdd(e: FormEvent) {
    e.preventDefault();

    const input = inputRef.current;
    const value = input?.value ?? "";
    if (!value.trim()) return;

    addItem(spaceId, list.id, value);

    if (input) input.value = "";
    setHasText(false);

    /*
     * Straight into the next one.
     *
     * A shopping list is written as one item after another without stopping,
     * and the button that adds them takes the focus off the field to do it —
     * so on a phone every «Добавить» put the keyboard away and asked for
     * another tap to get it back. Enter never had that problem, which is
     * exactly why it went unnoticed on a laptop.
     *
     * Inside the gesture that submitted, so the keyboard is allowed to stay:
     * a browser only lets a script raise it while it is still handling a real
     * press.
     */
    input?.focus();

    /*
     * And back into view, because the row that was just added pushed the field
     * down by its own height.
     *
     * With a keyboard up there are only a few hundred pixels of window: after
     * six or seven items the field had walked underneath it and nothing
     * brought it back. `nearest` rather than `center`, so a field already
     * comfortably on screen is left exactly where it is.
     */
    requestAnimationFrame(() => {
      input?.scrollIntoView({ block: "nearest" });
    });
  }

  /**
   * Open, the meta is the tally it has always been. Folded, it has to carry the
   * whole list: the count says what is left rather than what is finished,
   * because that is the number somebody folds a list to stop looking at.
   */
  const summary = open
    ? total > 0
      ? `${done} / ${total}`
      : undefined
    : total === 0
      ? t("lists.summaryEmpty")
      : left === 0
        ? t("lists.summaryAllDone")
        : t(REMAINING[list.kind], { count: left, total });

  return (
    <Card ref={cardRef}>
      {/*
        The heading *is* the switch. The button sits inside the `h2` rather
        than around it — a heading is not phrasing content, so a button may not
        hold one — and the list keeps its heading either way.
      */}
      {renaming ? (
        /* In place of the heading, not under it: renaming a list is the
           heading becoming editable, and a form that appears somewhere else
           makes you look for what it belongs to. */
        <RenameList
          list={list}
          onDone={(title) => {
            if (title) setListTitle(spaceId, list, title);
            setRenaming(false);
            setActions(false);
          }}
        />
      ) : (
        <div className="relative">
          {/* Behind the row, and covered by it until it moves. No `hidden`,
              because the row is opaque and does the hiding — a panel that
              appears and disappears cannot be slid onto. */}
          <div
            id={actionsId}
            aria-hidden={!actions}
            className="absolute inset-y-0 right-0 flex items-stretch"
          >
            <button
              type="button"
              tabIndex={actions ? 0 : -1}
              onClick={() => setRenaming(true)}
              aria-label={t("lists.rename")}
              className="grid w-14 place-items-center bg-surface-alt text-ink-soft transition-colors hover:text-ink"
            >
              <EditIcon aria-hidden="true" className="size-[20px]" strokeWidth={1.8} />
            </button>
            {/* `neg` and nothing else on the card is: the one destructive thing
                here, and the only place the colour is earned. */}
            {/* Between renaming and deleting, which is where it belongs on the
                scale: out of the way is not gone. */}
            <button
              type="button"
              tabIndex={actions ? 0 : -1}
              onClick={() => {
                archiveList(spaceId, list.id, !list.archived);
                setActions(false);
              }}
              aria-label={t(list.archived ? "lists.unarchive" : "lists.archive")}
              className="grid w-14 place-items-center bg-surface-alt text-ink-soft transition-colors hover:text-ink"
            >
              <ArchiveIcon aria-hidden="true" className="size-[20px]" strokeWidth={1.8} />
            </button>
            <button
              type="button"
              tabIndex={actions ? 0 : -1}
              onClick={() => setConfirming(true)}
              aria-label={t("lists.removeList")}
              className="grid w-14 place-items-center bg-neg-soft text-neg"
            >
              <TrashIcon aria-hidden="true" className="size-[20px]" strokeWidth={1.8} />
            </button>
          </div>

          {/* The swipe listens on the row around both buttons, so the gesture
              works anywhere along the heading and not only on the part that
              folds. */}
          <div
            className={cn(
              "relative flex items-center bg-surface transition-transform duration-200",
              actions && "-translate-x-[10.5rem]",
            )}
            {...swipe}
          >
            <h2 className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => (actions ? setActions(false) : onToggle())}
                aria-expanded={actions ? undefined : open}
                aria-controls={actions ? undefined : bodyId}
                aria-label={actions ? t("lists.closeActions", { title: list.title }) : undefined}
                className="flex min-h-[var(--touch)] w-full items-center gap-3 py-3 pr-1 pl-5 text-left transition-colors hover:bg-surface-alt"
              >
                {list.icon ? (
                  <span aria-hidden="true" className="text-lg leading-none">
                    {list.icon}
                  </span>
                ) : null}
                <span className="min-w-0 flex-1 truncate font-display text-[1.0625rem]">
                  {list.title}
                </span>
                {summary ? (
                  /* Finishing a list is worth a mark, and a tick in the accent
                     is the quietest one available. */
                  <span
                    className={cn(
                      "flex shrink-0 items-center gap-1 text-[0.8125rem]",
                      complete ? "text-accent" : "text-muted",
                    )}
                  >
                    {complete ? (
                      <CheckIcon
                        aria-hidden="true"
                        className="size-[15px]"
                        strokeWidth={2.4}
                      />
                    ) : null}
                    {summary}
                  </span>
                ) : null}
                <ChevronDownIcon
                  aria-hidden="true"
                  strokeWidth={1.9}
                  className={cn(
                    "size-[18px] shrink-0 text-muted transition-transform",
                    open && "rotate-180",
                  )}
                />
              </button>
            </h2>

            {/* The findable half of the pair. Named with the list in it,
                because a screen of five cards otherwise offers five buttons
                called the same thing. Gone while the row is aside: the two
                buttons it reveals are right there, and a third would be one
                too many. */}
            <button
              type="button"
              onClick={() => {
                setActions((shown) => !shown);
                setRenaming(false);
              }}
              aria-expanded={actions}
              aria-controls={actionsId}
              aria-label={t("lists.actions", { title: list.title })}
              className={cn(
                "mr-2 grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-opacity hover:bg-surface-alt hover:text-ink",
                actions && "pointer-events-none opacity-0",
              )}
            >
              <EditIcon className="size-[18px]" strokeWidth={1.8} />
            </button>
          </div>
        </div>
      )}

      {/* The card's hairline, doing a second job.

          The divider under the heading was already there and already one pixel
          of tone; filling it from the left is the progress bar, at no cost in
          height. It says what the folded card says in words, which is why it is
          drawn only when the card is open — and why it is `aria-hidden`: the
          tally sits in the heading a centimetre away, and nobody needs to hear
          the same fraction twice.

          An empty list gets the plain hairline back. A track at nought is a
          promise there is something to fill. */}
      {open ? (
        total > 0 ? (
          <div aria-hidden="true" className="h-0.5 bg-line-soft">
            <div
              className="h-full bg-accent transition-[width] duration-300"
              style={{ width: `${Math.round((done / total) * 100)}%` }}
            />
          </div>
        ) : (
          <div aria-hidden="true" className="h-px bg-line-soft" />
        )
      ) : null}

      {confirming ? (
        <ConfirmRemoveList
          list={list}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            setActions(false);
            removeList(spaceId, list.id);
          }}
        />
      ) : null}

      {/* Folded, the rows come off the screen entirely — and this card's own
          `DndContext` goes with them, so there is no droppable left behind for
          an item to be dragged into and lost. Nothing crosses between cards in
          any case: each list drags inside its own context. */}
      <div id={bodyId} hidden={!open}>
        {open ? (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragEnd={onDragEnd}
            >
              <ul className="divide-y divide-line-soft">
                <SortableContext
                  items={draggable.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {ordered.map((item) => (
                    <ItemRow
                      key={item.id}
                      spaceId={spaceId}
                      userId={userId}
                      item={item}
                      kind={list.kind}
                      members={members}
                      openOnArrival={arrivedAt === item.id}
                      sortable={!item.done}
                      onToggle={() => toggleItem(spaceId, list.id, item.id, userId)}
                      onRemove={() => removeItem(spaceId, list.id, item.id)}
                    />
                  ))}
                </SortableContext>
              </ul>
            </DndContext>

            {/* Right under the ticked ones, which is where the question comes
                up: forty crossed-out lines with a field beneath them. Nothing
                at all until something is ticked. */}
            {done > 0 ? <ClearDone list={list} spaceId={spaceId} done={done} /> : null}

            {/* The add row is the list's next row, not a form bolted underneath — so
                it keeps the rows' gutter and their hairline divider, and nothing is
                drawn around it. */}
            <form
              onSubmit={onAdd}
              className="flex items-center gap-2 border-t border-line-soft py-1.5 pr-2 pl-3"
            >
              <PlusIcon
                aria-hidden="true"
                className="size-[20px] shrink-0 text-muted"
                strokeWidth={1.9}
              />
              <input
                ref={inputRef}
                defaultValue=""
                onChange={(e) => setHasText(e.target.value.trim().length > 0)}
                placeholder={t(PLACEHOLDER[list.kind])}
                aria-label={t("lists.addTo", { title: list.title })}
                /* «Ввод», not «Готово». This is the one field in the app that
                   is submitted ten times in a row, and a key promising the end
                   of typing is a key that lies about what happens next. */
                enterKeyHint="enter"
                className="min-h-[var(--touch)] min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted"
              />
              {hasText ? (
                <button
                  type="submit"
                  className="min-h-[var(--touch)] shrink-0 rounded-pill px-4 text-[0.9375rem] font-semibold text-accent transition-colors hover:bg-accent-soft"
                >
                  {t("common.add")}
                </button>
              ) : null}
            </form>
          </>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * What to do with everything already ticked.
 *
 * Two verbs, because there are two lists. «Снять отметки» is the weekly shop —
 * the same list used again, which is why items have no repeat of their own
 * (SPEC §4.2); that decision only holds if taking the marks off is one gesture.
 * «Убрать» is a list of tasks, where done means finished and finished means
 * gone.
 *
 * Unticking asks nothing: it is undone by ticking again. Removing asks twice,
 * the way everything destructive here does — and it says how many, because
 * «убрать выполненные» is a different sentence when it is three and when it is
 * forty.
 */
function ClearDone({
  list,
  spaceId,
  done,
}: {
  list: ListView;
  spaceId: string;
  done: number;
}) {
  const t = useT();
  const { clearDone } = useLists();
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(false), CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [confirming]);

  return (
    <div className="flex items-center gap-1 border-t border-line-soft py-1.5 pr-2 pl-3">
      <button
        type="button"
        onClick={() => clearDone(spaceId, list.id, "untick")}
        className="inline-flex min-h-[var(--touch)] items-center gap-1.5 rounded-pill px-3 text-[0.8125rem] font-medium text-ink-soft transition-colors hover:bg-surface-alt"
      >
        <UndoIcon aria-hidden="true" className="size-[17px] shrink-0" strokeWidth={1.8} />
        {t("lists.untickDone", { count: done })}
      </button>

      <span className="flex-1" />

      <button
        type="button"
        onClick={() => {
          if (!confirming) {
            setConfirming(true);
            return;
          }
          setConfirming(false);
          clearDone(spaceId, list.id, "remove");
        }}
        className={cn(
          "inline-flex min-h-[var(--touch)] items-center gap-1.5 rounded-pill px-3 text-[0.8125rem] font-medium transition-colors",
          confirming ? "bg-neg-soft text-neg" : "text-muted hover:bg-neg-soft hover:text-neg",
        )}
      >
        <TrashIcon aria-hidden="true" className="size-[17px] shrink-0" strokeWidth={1.8} />
        {confirming
          ? t("lists.removeDoneConfirm", { count: done })
          : t("lists.removeDone", { count: done })}
      </button>
    </div>
  );
}

/**
 * The list's name, edited where it is read.
 *
 * `onDone(null)` is "leave it alone" — an emptied field is not a rename, and
 * neither is closing the drawer. The same rule the item sheet applies to a
 * blank text box: the bin deletes, an empty field does not.
 */
function RenameList({
  list,
  onDone,
}: {
  list: ListView;
  onDone: (title: string | null) => void;
}) {
  const t = useT();
  const [title, setTitle] = useState(list.title);
  const trimmed = title.trim();

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onDone(trimmed && trimmed !== list.title ? trimmed : null);
      }}
      className="flex items-center gap-2 py-1.5 pr-2 pl-3"
    >
      <input
        autoFocus
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        aria-label={t("lists.renameLabel", { title: list.title })}
        enterKeyHint="done"
        maxLength={200}
        className="min-h-[var(--touch)] min-w-0 flex-1 rounded-row bg-surface-alt px-3 outline-none placeholder:text-muted"
      />
      <button
        type="button"
        onClick={() => onDone(null)}
        aria-label={t("common.cancel")}
        className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
      >
        <CloseIcon className="size-[20px]" strokeWidth={1.9} />
      </button>
      <button
        type="submit"
        disabled={!trimmed}
        className="min-h-[var(--touch)] shrink-0 rounded-pill px-4 text-[0.9375rem] font-semibold text-accent transition-colors hover:bg-accent-soft disabled:text-muted"
      >
        {t("common.save")}
      </button>
    </form>
  );
}

/**
 * Asking before a list goes.
 *
 * Deliberately heavier than the two-press bin a wish or an event gets, and
 * deliberately lighter than typing a space's name back. A wish is one row and
 * one person's plan; a space is everything. A list is in between and so is
 * this: a question that has to be read, naming the list and counting what goes
 * with it, answered by pressing rather than by typing. The count is the reason
 * — nobody folding a list shut remembers it still holds eleven things, and
 * there is no undo behind the outbox for either of them.
 */
function ConfirmRemoveList({
  list,
  onCancel,
  onConfirm,
}: {
  list: ListView;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const t = useT();
  const total = list.items.length;

  return (
    <ConfirmDialog
      title={t("lists.removeListTitle", { title: list.title })}
      confirmLabel={t("lists.removeList")}
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      <p>
        {total > 0 ? t("lists.removeListItems", { count: total }) : t("lists.removeListEmpty")}
      </p>
      <p className="text-neg">{t("lists.removeListBoth")}</p>
    </ConfirmDialog>
  );
}

function ItemRow({
  spaceId,
  userId,
  item,
  kind,
  members,
  openOnArrival,
  sortable,
  onToggle,
  onRemove,
}: {
  spaceId: string;
  userId: string;
  item: ListItemView;
  kind: ListKind;
  members: MemberView[];
  /** Arrived here from a search hit or the activity feed: open the card. */
  openOnArrival: boolean;
  sortable: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const formats = useDateFormats();
  // The card is mounted only while it is open. That keeps its hooks — tags,
  // chat, attachments, the router — out of every row of every list, and off the
  // screen entirely for anybody who never opens one.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (openOnArrival) setOpen(true);
  }, [openOnArrival]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !sortable });

  const assignee = item.assigneeId
    ? members.find((member) => member.user.id === item.assigneeId)?.user
    : undefined;

  /*
   * Deleting takes two presses, the way it does for a wish, an event and a
   * message.
   *
   * This was the one row in the app that went on a single tap, and it is the
   * most numerous row there is — sitting a thumb's width from a full-width
   * button that opens the details. There is no undo behind the outbox, so a
   * thumb landing here while scrolling must not be the whole story.
   *
   * The question goes away by itself, and anything else pressed on the row
   * takes it back — so a row left half-pressed is not still armed when the
   * screen comes back.
   */
  const [confirming, setConfirming] = useState(false);
  const removeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!confirming) return;
    const timer = setTimeout(() => setConfirming(false), CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [confirming]);

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      /* On pointerdown and captured, so it lands before the thing being
         pressed acts — the bin itself is excluded, or the second press would
         cancel what it came to confirm. */
      onPointerDownCapture={(event) => {
        if (!confirming) return;
        if (removeRef.current?.contains(event.target as Node)) return;
        setConfirming(false);
      }}
      className={cn(
        "group flex items-center gap-1.5 bg-surface py-1 pr-2 pl-1.5",
        // Lift the row being dragged above its neighbours, and stop the browser
        // from treating the gesture as a scroll on a touch screen.
        isDragging && "relative z-10 shadow-soft",
      )}
    >
      {sortable ? (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={t("lists.move", { text: item.text })}
          className="grid h-[var(--touch)] w-6 shrink-0 touch-none cursor-grab place-items-center rounded-pill text-line transition-colors group-hover:text-muted active:cursor-grabbing"
        >
          <GripIcon className="size-[18px]" strokeWidth={1.8} />
        </button>
      ) : (
        // A done item keeps the same gutter so the rows stay aligned, but it is
        // not draggable: it lives in its own group at the bottom.
        <span aria-hidden="true" className="h-[var(--touch)] w-6 shrink-0" />
      )}

      {/* Round, because a square checkbox belongs to a different language. Empty
          it is a ring — the one place a ring is allowed, on a control rather
          than around a surface; filled it is the accent carrying the tick. */}
      <button
        type="button"
        role="checkbox"
        aria-checked={item.done}
        onClick={onToggle}
        className="grid size-[var(--touch)] shrink-0 place-items-center"
      >
        <span
          className={cn(
            "grid size-[22px] place-items-center rounded-pill transition-colors",
            item.done
              ? "bg-accent text-ground"
              : "text-transparent ring-1 ring-line",
          )}
        >
          <CheckIcon className="size-[14px]" strokeWidth={2.4} />
        </span>
        <span className="sr-only">{item.text}</span>
      </button>

      {/* The text is the way in, and nothing else on the row is: the checkbox,
          the handle and the bin keep doing exactly what they did. `span`s
          rather than `p`s because a button may only hold phrasing content —
          the shape on screen is unchanged. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("lists.detailsOf", { text: item.text })}
        className="min-h-[var(--touch)] min-w-0 flex-1 rounded-row px-1.5 py-2.5 text-left transition-colors hover:bg-surface-alt"
      >
        <span
          className={cn(
            "block break-words",
            item.done && "text-muted line-through decoration-line",
          )}
        >
          {item.text}
        </span>
        {item.note ? (
          <span className="mt-0.5 block text-[0.8125rem] text-muted">{item.note}</span>
        ) : null}
        {(assignee || item.dueAt) && !item.done ? (
          <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.75rem]">
            {assignee ? (
              <span className="rounded-pill bg-accent-soft px-2.5 py-0.5 text-accent-ink">
                {assignee.displayName}
              </span>
            ) : null}
            {/* A deadline is the thing to notice, and here noticing is amber. */}
            {item.dueAt ? (
              <span className="tabular text-warn">
                {/* The hour only when there is one to show — most deadlines are
                    a day, and «до 24 авг, 12:00» would be an hour nobody chose
                    dressed up as one they did. */}
                {t("lists.due", {
                  date:
                    timeValue(item.dueAt) === ""
                      ? formats.dayMonthShort.format(new Date(item.dueAt))
                      : `${formats.dayMonthShort.format(new Date(item.dueAt))}, ${timeValue(item.dueAt)}`,
                })}
              </span>
            ) : null}
          </span>
        ) : null}
      </button>

      <button
        ref={removeRef}
        type="button"
        onClick={() => (confirming ? onRemove() : setConfirming(true))}
        aria-label={
          confirming
            ? t("lists.removeConfirm", { text: item.text })
            : t("lists.remove", { text: item.text })
        }
        className={cn(
          "inline-flex min-h-[var(--touch)] shrink-0 items-center justify-center gap-1.5",
          "rounded-pill transition-colors",
          confirming
            ? "bg-neg-soft px-3 text-[0.8125rem] font-semibold text-neg"
            : "size-[var(--touch)] text-muted/60 hover:bg-neg-soft hover:text-neg",
        )}
      >
        <TrashIcon className="size-[18px] shrink-0" strokeWidth={1.8} />
        {confirming ? t("common.confirmDelete") : null}
      </button>

      {open ? (
        <ItemSheet
          spaceId={spaceId}
          userId={userId}
          item={item}
          kind={kind}
          members={members}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </li>
  );
}

/* ------------------------------------------------------------------- card */

/**
 * Writing to an item from here rather than through `useLists`.
 *
 * Everything below goes through the outbox exactly as the hook does — same
 * codec, same operations, same optimistic overlay. It sits in this file only
 * because `app/lists.tsx` was closed for this round; `setContent`, `setDueAt`
 * and `setAssignee` belong on `ListsContextValue` beside `toggleItem`, and
 * moving them there is a lift-and-drop with no change to the call sites.
 */
function setContent(spaceId: string, itemId: string, content: ListItemContent): void {
  const codec = syncEngine.codec(spaceId);
  if (!codec) return;

  void codec.encode(content).then((blob) => {
    syncEngine.mutate(operations.updateItem(spaceId, itemId, { content: blob }));
  });
}

/**
 * Renaming a list, written the same way and for the same reason as above.
 *
 * The icon is carried across rather than dropped: the title and the icon share
 * one blob, so writing `{ title }` alone would quietly take the emoji off every
 * list anybody renamed.
 *
 * `renameList` belongs on `ListsContextValue` next to `removeList`, which is
 * already there and is what the deletion below calls.
 */
function setListTitle(spaceId: string, list: ListView, title: string): void {
  const codec = syncEngine.codec(spaceId);
  if (!codec) return;

  const content: ListContent = { title, ...(list.icon ? { icon: list.icon } : {}) };

  void codec.encode(content).then((blob) => {
    syncEngine.mutate(operations.updateList(spaceId, list.id, { content: blob }));
  });
}

function setDueAt(spaceId: string, itemId: string, dueAt: string | null): void {
  syncEngine.mutate(operations.updateItem(spaceId, itemId, { dueAt }));
}

/**
 * The preview's `body` is capped at 240 characters (`notificationPreviewSchema`)
 * and an item's text may be two thousand. An over-long body would fail the
 * service worker's parse and silently cost the notification its words, so it is
 * cut here, where it is written.
 */
const PREVIEW_BODY_MAX = 240;

function clamp(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/**
 * Assigning, and the one change on this card somebody else needs to hear about.
 *
 * The words are composed here, next to the action, and go through the same
 * codec as the item itself — so in an encrypted space the server forwards a
 * blob it cannot read and the other device decrypts it (SPEC §3, decision 3).
 * The title is the same line the service worker falls back to, so the push
 * reads the same whether or not the text could be decoded; which task it is
 * goes in the body, where there is room for it.
 *
 * Two cases say nothing at all. Taking an assignment off is not news anybody is
 * waiting for, and taking a task on yourself is news to nobody — `notifySpace`
 * skips whoever acted, so a self-assignment would reach only the other person,
 * telling them a task was assigned that was never theirs.
 */
function setAssignee(
  spaceId: string,
  item: { id: string; text: string },
  self: string,
  assigneeId: string | null,
): void {
  if (assigneeId === null || assigneeId === self) {
    syncEngine.mutate(operations.updateItem(spaceId, item.id, { assigneeId }));
    return;
  }

  // Below the branch above on purpose: those two cases write no content at all,
  // so they are none of the codec's business and keep working in a space this
  // device cannot write into.
  const codec = syncEngine.codec(spaceId);
  if (!codec) return;

  void codec
    .encode({
      title: t("notify.itemAssigned"),
      body: clamp(item.text, PREVIEW_BODY_MAX),
    })
    .then((preview) => {
      syncEngine.mutate(
        operations.updateItem(
          spaceId,
          item.id,
          { assigneeId },
          // Addressed, not announced: only the person it was given to. Told to
          // the room, "a task for you" is true for one of them and a lie to the
          // rest — which nobody notices in a space of two.
          { type: "item.assigned", targetType: "item", preview, userId: assigneeId },
        ),
      );
    });
}

/** `YYYY-MM-DD` in local time — what an `<input type="date">` reads and writes. */
function dayValue(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** `HH:MM` in local time, or empty when the deadline is the day and no more. */
function timeValue(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  // Midday is what a day-only deadline is stored as (see `isoFromDay`), so it
  // reads back as "no time given" — which is what somebody who never opened
  // the time field meant.
  if (date.getHours() === 12 && date.getMinutes() === 0) return "";

  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${hours}:${minutes}`;
}

/**
 * A day, and an hour if one was given.
 *
 * Midday when it was not, and component-wise rather than
 * `new Date("2026-08-24")` — which the spec reads as UTC and would land a
 * deadline on the day before for anybody east of Greenwich. Midday leaves
 * twelve hours of slack either way, so the two people in a space read the same
 * date off the same item even from different time zones.
 *
 * The hour exists because the deadline is also what the reminder counts back
 * from. Without it «за два часа» meant ten in the morning whatever anybody had
 * in mind by «до 24 августа» — a notice for a thing due at six in the evening,
 * arriving before breakfast.
 */
/**
 * Whether a date is finished, or still being typed.
 *
 * A browser reports `<input type="date">` as empty until every segment has
 * something in it — and then, cruelly, calls a year of «2» finished: the value
 * comes out as `0002-12-12`, which is a perfectly valid date and a completely
 * different year from the one being typed. Writing that away and reading it
 * back is what replaced the year under somebody's caret with 1902 (JavaScript
 * maps two-digit years into the twentieth century).
 *
 * So: four digits of year, or nobody has finished. Nothing a couple plans
 * happens before the year one thousand.
 */
function readyDay(value: string): boolean {
  return Number(value.slice(0, 4)) >= 1000;
}

function isoFromDay(value: string, time = ""): string | null {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;

  const [hours, minutes] = time ? time.split(":").map(Number) : [12, 0];

  const date = new Date(year, month - 1, day, hours ?? 12, minutes ?? 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** A field's label, sitting above whatever it labels. Same shape as a card row. */
function Field({
  label,
  hint,
  className,
  children,
}: {
  label: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="px-1 text-[0.8125rem] font-medium text-ink-soft">
        {label}
        {hint ? <span className="ml-1.5 font-normal text-muted">{hint}</span> : null}
      </span>
      {children}
    </label>
  );
}

/** Inside the card the ground is `surface`, so a field is a step up from it. */
const FIELD =
  "min-h-[var(--touch)] w-full rounded-row bg-surface-alt px-4 py-2.5 " +
  "outline-none transition-colors placeholder:text-muted";

const CHOICE =
  "inline-flex min-h-[var(--touch)] items-center rounded-pill px-3.5 " +
  "text-[0.8125rem] transition-colors";
const CHOICE_ON = "bg-accent-soft font-semibold text-accent-ink";
const CHOICE_OFF = "bg-surface-alt font-medium text-muted hover:text-ink-soft";

/**
 * Everything an item is, on a sheet that slides up over the screen.
 *
 * The row is four things wide — text, tick, handle, bin — and the server has
 * accepted a note, a deadline and an assignee since the first phase with
 * nowhere on the screen to set them. Assigning was the worst of it: the column
 * existed, the API took it, the row drew the name, and no gesture anywhere put
 * one there.
 *
 * A sheet rather than a route because it is a detour, not a place: what is
 * being edited stays visible behind the dimming, and closing puts the person
 * back exactly where they were in a long list.
 */
function ItemSheet({
  spaceId,
  userId,
  item,
  kind,
  members,
  onClose,
}: {
  spaceId: string;
  userId: string;
  item: ListItemView;
  /** Only for the wording: «Дело» in one preset and «Покупка» in the other. */
  kind: ListKind;
  members: MemberView[];
  onClose: () => void;
}) {
  const t = useT();
  const navigate = useNavigate();
  const tagging = useTags(spaceId);
  const chat = useChat(spaceId, userId, null);

  /**
   * Text and note are drafted; the date and the assignee are not.
   *
   * A picker is one gesture and there is nothing to lose by sending it at once.
   * Typing is different: sending every keystroke would fill the outbox with a
   * hundred versions of one sentence, so the draft is committed on blur and
   * again on the way out — closing by tapping the dimmed background must not
   * quietly throw away what was just typed.
   */
  const [text, setText] = useState(item.text);
  const [note, setNote] = useState(item.note ?? "");

  const draft = useRef({ text, note });
  draft.current = { text, note };

  const commit = useCallback(() => {
    const nextText = draft.current.text.trim();
    const nextNote = draft.current.note.trim();

    // An empty item is not a thing: the bin deletes, a blank field does not.
    if (!nextText) {
      setText(item.text);
      return;
    }

    if (nextText === item.text && nextNote === (item.note ?? "")) return;

    setContent(spaceId, item.id, {
      text: nextText,
      ...(nextNote ? { note: nextNote } : {}),
    });
  }, [spaceId, item.id, item.text, item.note]);

  const close = useCallback(() => {
    commit();
    onClose();
  }, [commit, onClose]);

  const discussion = chat.discussionFor({ type: "item", id: item.id });

  /**
   * Open the discussion about this item, starting it if nobody has — the same
   * gesture a wish has, so a question about a task lands in the one place
   * conversations live rather than in a second message list here.
   */
  function discuss() {
    const title = text.trim() || item.text;
    const id = discussion?.id ?? chat.createThread(title, { type: "item", id: item.id });
    if (!id) return;

    commit();
    onClose();
    navigate(`/chat?topic=${id}`);
  }

  /**
   * The text the person can see, rather than the one last saved.
   *
   * A rename is committed on blur, and tapping a pill is what blurs the field —
   * so the committed value has not come back round through the overlay by the
   * time this click runs, and `item.text` would put the old wording in a
   * notification about the new task.
   */
  const assign = useCallback(
    (assigneeId: string | null) => {
      const current = { id: item.id, text: text.trim() || item.text };
      setAssignee(spaceId, current, userId, assigneeId);
    },
    [spaceId, item.id, item.text, text, userId],
  );

  /*
   * The deadline is typed, not picked, and that changes everything.
   *
   * `<input type="date">` reports a value only once the whole date is valid.
   * Half a date is `""` — and this used to write straight through on every
   * keystroke, so typing «30.08.2026» sent `null` after «30», the store
   * answered with an empty field, and the two digits already typed were wiped.
   * The only orders that survived were the ones where the last segment
   * happened to complete the value: year first, or minutes first.
   *
   * So the fields hold their own value and the store hears from them at two
   * moments: when the value is complete, and when the field is left. An empty
   * value is a clear, but only on the way out — while the caret is still in
   * there it is somebody halfway through typing.
   */
  const stored = {
    day: item.dueAt ? dayValue(item.dueAt) : "",
    time: item.dueAt ? timeValue(item.dueAt) : "",
  };
  const [typed, setTyped] = useState(stored);
  /** Whether a caret is in one of these two fields right now. */
  const [editing, setEditing] = useState(false);

  /*
   * What the other person did to this item arrives through the store and has
   * to show — but never over the top of a field somebody is typing in.
   *
   * This is not a nicety. Chrome calls a year of «20» a finished date (0020),
   * so a value would be written, come back through the store as something
   * else entirely, and replace the year mid-keystroke. `readyDay` below stops
   * that particular write; this stops the whole class of them.
   */
  const seen = useRef(item.dueAt);
  if (!editing && seen.current !== item.dueAt) {
    seen.current = item.dueAt;
    if (typed.day !== stored.day || typed.time !== stored.time) setTyped(stored);
  }

  const due = typed.day;
  const dueTime = typed.time;

  const writeDue = (day: string, time: string) => {
    setDueAt(spaceId, item.id, day ? isoFromDay(day, time) : null);
  };

  const held = { onFocus: () => setEditing(true), onBlur: () => setEditing(false) };

  /*
   * The one shape the app has, and not a second copy of it.
   *
   * This sheet was written by hand before `Sheet` existed and stayed that way:
   * same idea, own dimming, own escape key, own scroll lock — and, once the
   * others grew a handle and a drag, the only modal in the app you could not
   * take hold of. Everything below the header is unchanged; everything above
   * it is now the same code as the wish, the event and the place.
   *
   * No «свернуть в черновик»: this edits something that already exists and
   * every field here saves itself, so there is nothing half-written to keep.
   * Dragging it down closes it, which is what it did already.
   */
  return (
    <Sheet title={item.text} onClose={close}>
      <div className="flex flex-col gap-4 px-4 py-4">
          <Field label={t(ITEM_LABEL[kind])}>
            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              onBlur={commit}
              rows={2}
              className={cn(FIELD, "resize-none")}
            />
          </Field>

          <Field label={t("lists.fieldNote")} hint={t("common.optional")}>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              onBlur={commit}
              rows={3}
              placeholder={t("lists.notePlaceholder")}
              className={cn(FIELD, "resize-none")}
            />
          </Field>

          {/* The reset sits beside the label rather than inside it: a `<label>`
              holding a second button forwards a tap on that button to the date
              input as well, so clearing the date would reopen the picker. */}
          <div className="flex items-end gap-2">
            <Field
              label={t("lists.fieldDue")}
              hint={t("common.optional")}
              className="flex-1"
            >
              <div className="flex gap-2">
                <input
                  type="date"
                  value={due}
                  {...held}
                  onChange={(event) => {
                    const day = event.target.value;
                    setTyped((current) => ({ ...current, day }));
                    // Only a whole date. An empty one here is a date being
                    // typed, and so is one whose year is two digits long.
                    if (readyDay(day)) writeDue(day, dueTime);
                  }}
                  onBlur={(event) => {
                    setEditing(false);
                    if (!event.target.value) writeDue("", "");
                  }}
                  className={cn(FIELD, "flex-1")}
                />
                {/* Only once there is a day to put it on: an hour with no date
                    is not a deadline, and the field would be a question with
                    nowhere to put the answer. */}
                {due ? (
                  <input
                    type="time"
                    value={dueTime}
                    aria-label={t("lists.fieldDueTime")}
                    {...held}
                    onChange={(event) => {
                      const time = event.target.value;
                      setTyped((current) => ({ ...current, time }));
                      // Same trap, one field over: «12» with no minutes yet is
                      // reported as nothing at all.
                      if (time) writeDue(due, time);
                    }}
                    onBlur={(event) => {
                      setEditing(false);
                      // An hour taken back leaves the day: «до 30 августа» is
                      // a deadline, «в 12:00 неизвестно какого числа» is not.
                      if (!event.target.value) writeDue(due, "");
                    }}
                    className={cn(FIELD, "w-[7.5rem] shrink-0")}
                  />
                ) : null}
              </div>
            </Field>
            {/* Clearing a date is not destructive, so it is not `neg` — it is
                simply the way back to "no deadline". */}
            {due ? (
              <button
                type="button"
                onClick={() => {
                  setTyped({ day: "", time: "" });
                  setDueAt(spaceId, item.id, null);
                }}
                aria-label={t("lists.dueClear")}
                className="grid size-[var(--touch)] shrink-0 place-items-center rounded-pill text-muted transition-colors hover:bg-surface-alt hover:text-ink"
              >
                <CloseIcon className="size-[18px]" strokeWidth={1.8} />
              </button>
            ) : null}
          </div>

          {/* The point of the card. A space is two people, so this is two pills
              and «никто» — a select would be a heavier control for a shorter
              list than it can fill. */}
          <div className="flex flex-col gap-1.5">
            <span className="px-1 text-[0.8125rem] font-medium text-ink-soft">
              {t("lists.fieldAssignee")}
            </span>
            <div
              role="group"
              aria-label={t("lists.fieldAssignee")}
              className="flex flex-wrap gap-1.5"
            >
              <button
                type="button"
                aria-pressed={item.assigneeId === null}
                onClick={() => assign(null)}
                className={cn(
                  CHOICE,
                  item.assigneeId === null ? CHOICE_ON : CHOICE_OFF,
                )}
              >
                {t("lists.assigneeNobody")}
              </button>
              {members.map((member) => {
                const on = item.assigneeId === member.user.id;
                return (
                  <button
                    key={member.user.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => assign(on ? null : member.user.id)}
                    className={cn(CHOICE, on ? CHOICE_ON : CHOICE_OFF)}
                  >
                    {on ? (
                      <CheckIcon
                        className="mr-1.5 size-[18px] shrink-0"
                        strokeWidth={1.9}
                        aria-hidden="true"
                      />
                    ) : null}
                    {member.user.displayName}
                  </button>
                );
              })}
            </div>
          </div>

          {/* The same three rows a wish carries, so «подарки» means the same
              thing on an item as on a wish and a photo of the shelf lives on
              the task rather than in a thread about it (SPEC §4.6). */}
          <TagPicker
            selected={tagging.tagsOf("item", item.id)}
            available={tagging.tags}
            onLink={(tagId) => tagging.link(tagId, "item", item.id)}
            onUnlink={(tagId) => tagging.unlink(tagId, "item", item.id)}
            onCreate={(name, color) => tagging.createOn(name, "item", item.id, color)}
            label={item.text}
          />

          <AttachmentStrip spaceId={spaceId} target={{ type: "item", id: item.id }} compact />

          <button
            type="button"
            onClick={discuss}
            className="inline-flex min-h-[var(--touch)] w-fit items-center gap-1.5 rounded-pill px-2 text-[0.8125rem] text-muted transition-colors hover:text-accent"
          >
            <ChatIcon className="size-[18px]" strokeWidth={1.9} />
            {discussion
              ? t("lists.discussionCount", { count: discussion.messageCount })
              : t("lists.discuss")}
          </button>
      </div>
    </Sheet>
  );
}
